import {
  getAttributeValue,
  getBreadcrumbs,
  getCategoriesByRoute,
  getFirstAttributeValue,
  getLowestPrice,
  getPrice,
  type RpcMethodParameters,
  type Price,
  type ProductCategory as BapiProductCategory,
  type Product,
  type Variant,
} from '@scayle/storefront-nuxt'

type Options = Partial<{
  params: MaybeRefOrGetter<Partial<RpcMethodParameters<'getProductById'>>>
  recommendationAttributes: ProductRecommendationAttribute[]
  options?: Parameters<typeof useRpc<'getProductById'>>[3]
}>

export const useProductDetails = (
  baseProduct: MaybeRefOrGetter<Product>,
  {
    params = {
      includeSellableForFree: true,
      with: PRODUCT_WITH_PARAMS,
    },
    options = {},
  }: Options = {},
) => {
  const instance = useNuxtApp()
  const key = `product-details-${toValue(baseProduct).id}`
  const { $config, $featureToggle, $fimLocalePath } = instance
  const { isRxConfiguratorEnabled } = $featureToggle
  const { getProductDetailRoute } = useRouteHelpers()
  const route = useRoute()
  const productId = computed(() => {
    const id = String(route.params.slug).split('-').pop() as string
    return Number.parseInt(id, 10)
  })

  const variantId = ref(getQueryParam(route.query, 'variantid'))

  const wishlist = useWishlist()
  const productPromise = useProduct({
    params: {
      id: productId.value,
      includeSellableForFree: toValue(params)?.includeSellableForFree ?? true,
      includeSoldOut: toValue(params)?.includeSoldOut ?? false,
      pricePromotionKey: toValue(params)?.pricePromotionKey,
      with: toValue(params)?.with ?? PRODUCT_WITH_PARAMS,
    },
    key: `${key}-useFimProductDetails-${productId.value}`,
    options,
  })
  const trackingEventsPromise = useTrackingEvents()
  const productPricePromise = useProductPrice()
  const { isRopoSupported } = useRopoBasketState()

  // this promise will be awaited when the composable is awaited
  const finalPromise = Promise.all([
    wishlist,
    // set packageUnit as soon as we have variants
    productPromise.then(() => {
      if (variantId.value) {
        setVariantId(variantId.value)
      } else {
        setVariantId(cheapestAvailableVariant.value?.id)
      }
    }),
    trackingEventsPromise,
    productPricePromise,
  ])

  const {
    fetch,
    data: completeProduct,
    error,
    fetching,
    status,
  } = productPromise
  const { trackViewItem } = trackingEventsPromise
  const { getCombinedProductPrice } = productPricePromise

  const product = computed(() => {
    if (status.value === 'success') {
      return toValue(completeProduct)
    }

    return toValue(baseProduct)
  })

  // initial value is '0', as soon as the product promise (with variants) is resolved
  // we update the packageUnit value
  const packageUnit = useState('packageUnit', () => '0')

  const trackProductView = (product: Product) => {
    if (product) {
      try {
        const isRopo = isRopoSupported(product)
        const overridePageType = isRopo
          ? isFromRopoPlp.value
            ? 'product_ropo_only'
            : 'product_ropo'
          : 'product'
        trackViewItem(product, { overridePageType })
      } catch (e) {
        // todo add logger after memory-leak-6 is merged
      }
    }
  }

  const {
    brand,
    name: productName,
    variantWithLowestPrice,
    siblings,
  } = useProductBaseInfo(product)

  const variantsSortedByPrice: Ref<Variant[]> = computed(() =>
    (product?.value?.variants ?? []).sort(
      (a, b) => a.price.withTax - b.price.withTax,
    ),
  )

  const activeVariant = computed(() =>
    product.value?.variants?.find(
      (variant) => variant.id === Number(variantId.value),
    ),
  )

  const selectedVariant = computed(() => {
    return (
      (variantsSortedByPrice.value
        ? variantsSortedByPrice.value[parseInt(packageUnit.value, 10) || 0]
        : activeVariant.value) || undefined
    )
  })

  const cheapestAvailableVariant = computed(() =>
    variantsSortedByPrice.value.find((v) => {
      return isVariantAvailable(product.value, v)
    }),
  )

  const currentVariantReferenceKey = computed(() =>
    product.value?.variants
      ? (product?.value?.variants[parseInt(packageUnit.value, 10) || 0]
          ?.referenceKey ?? '')
      : '',
  )

  const hasSpecial = computed(() =>
    Boolean(!selectedVariant.value && price.value?.appliedReductions?.length),
  )

  const deliveryForecast = computed(
    () =>
      getFirstAttributeValue(product.value?.attributes, 'deliveryForecast')
        ?.label,
  )

  const brandLogo = computed(() =>
    getAttributeValue(product.value?.attributes, 'brandLogo'),
  )

  // Redirecting from numeric ID to new pretty URL
  const setVariantId = (id: number | string | undefined) => {
    const _id = typeof id === 'string' ? Number.parseInt(id, 10) : id
    if (variantsSortedByPrice.value && _id) {
      const index = variantsSortedByPrice.value?.findIndex(
        (variant) => variant.id === _id,
      )

      packageUnit.value = index < 0 ? '0' : `${index}`
    } else {
      const index = variantsSortedByPrice.value?.findIndex(
        (variant) => variant.id === cheapestAvailableVariant.value?.id,
      )

      if (index >= 0) {
        variantId.value = cheapestAvailableVariant.value?.id.toString()
      }

      packageUnit.value = index < 0 ? '0' : `${index}`
    }
  }

  const quantity = useState<number>(
    `product-quantity-${key}-${productId.value}`,
    () => 1,
  )

  if (product.value && isNumericProductIdRoute(route.fullPath)) {
    // Replace Numeric ID Path with Pretty Path (Product Attributes added)
    const fullRoute = getProductDetailRoute(product.value)

    const fullSlug = fullRoute?.params?.slug as string
    if (fullSlug && !route.fullPath.includes(fullSlug)) {
      navigateTo({
        path: `/p/${fullSlug}/`,
        query: { ...route.query, variantid: variantId.value },
      })
    }
  }

  const lowestPriorPrice = computed(
    () =>
      activeVariant.value?.lowestPriorPrice ||
      variantWithLowestPrice.value?.lowestPriorPrice ||
      product.value?.lowestPriorPrice,
  )

  const rxConfiguratorEnabled = computed(() =>
    isRxConfiguratorEnabled(product.value as Product),
  )

  const price = computed(() => {
    if (rxConfiguratorEnabled.value && product.value) {
      return unref(
        getCombinedProductPrice(product.value, unref(selectedVariant)),
      )
    }
    if (selectedVariant.value) {
      return getPrice(selectedVariant.value)
    }

    if (product.value?.variants?.length) {
      return getLowestPrice(product.value?.variants)
    }

    return product.value?.priceRange?.max ?? ({} as Price)
  })

  const hasOneSizeVariantOnly = computed(() => {
    const variants = product.value?.variants
    return (
      variants?.length === 1 &&
      getAttributeValue(variants[0].attributes, 'size') === ONE_SIZE_KEY
    )
  })

  const image = useImage()

  const images = computed(() => {
    const options = {
      sizes: 'sm:100vw md:100vw',
      modifiers: { quality: PRODUCT_IMAGE_QUALITY_MODIFIER },
      provider: 'default',
    }
    const images = product.value?.images ?? []
    return images.map(({ hash }) => image?.getImage(hash, options).url) || []
  })

  const stock = computed(() => selectedVariant.value?.stock)
  const inStock = computed(() => stock.value?.quantity || 0)

  const isSellableWithoutStock = computed(
    () => inStock.value === 0 && Boolean(stock.value?.isSellableWithoutStock),
  )

  const availableQuantity = computed(() => {
    return getQuantitySelectionList(activeVariant.value?.stock.quantity, true)
  })

  const productCategories = computed(() => {
    return product.value ? getCategoriesByRoute(product.value, null) : []
  })

  const breadcrumbs = computed(() => getBreadcrumbs(productCategories.value))

  const hasSiblings = computed(
    () => product.value?.siblings && product.value?.siblings.length > 0,
  )

  const { pageState } = usePageState()

  const matchesLastCategory = computed(() => {
    const lastPath = String(pageState.value.lastCategoryRoute.fullPath)
    const tailingSlashRegxp = /\/$/g
    return product.value?.categories?.some((categories) => {
      return categories.some(
        (entry) =>
          entry.categoryUrl === lastPath?.replace(tailingSlashRegxp, ''),
      )
    })
  })

  const isFromRopoPlp = computed(
    () => pageState.value.lastCategoryRoute?.isRopoPlp,
  )

  const queryParams = route.redirectedFrom?.fullPath?.split('?')[1]
  let hasQueryParams = false

  if (queryParams && queryParams.length > 0) {
    hasQueryParams = true
  }

  const backUrl = computed(() => {
    if (isFromRopoPlp.value) {
      return $fimLocalePath(routeList.ropoPlp)
    }

    if (
      pageState.value.lastCategoryRoute.fullPath &&
      matchesLastCategory.value
    ) {
      return pageState.value.lastCategoryRoute.fullPath as string
    }

    if (hasQueryParams && route.redirectedFrom) {
      return route.redirectedFrom.fullPath
    }

    const shouldRedirectToFirstSubCategory = (
      categories: BapiProductCategory[],
    ) => {
      const sunglassesCategoryId = 2
      const accessoriesCategoryId = 76

      const mainCategory = categories[0]
      const hasSubCategories = categories.length > 1

      return (
        hasSubCategories &&
        mainCategory.categoryId === sunglassesCategoryId &&
        categories.some(
          ({ categoryId }) => categoryId === accessoriesCategoryId,
        )
      )
    }

    if (productCategories.value?.length) {
      const isRopo = isRopoSupported(product.value)
      const categories =
        productCategories.value?.filter((category) => {
          return (
            isRopo ||
            category.categoryUrl.replace(/\/$/, '') !==
              routeList.ropoPlp.path.replace(/\/$/, '')
          )
        }) ?? []

      const breadcrumbs = getBreadcrumbs(categories) || []
      const breadcrumbIndex = shouldRedirectToFirstSubCategory(
        productCategories.value,
      )
        ? 1
        : 0
      const url = breadcrumbs?.[breadcrumbIndex]?.to || '/'
      return url.endsWith('/') ? url : `${url}/`
    }
    return '/'
  })

  const isInWishlist = computed(() => {
    return wishlist.contains({ productId: productId.value })
  })

  const currentProductCategory = computed(() => {
    if (product.value) {
      return getProductCategory(product.value)
    }
  })

  const salesStatus = computed(() => getProductLifecycle(product))

  const manualUrl = computed<string | undefined>(() => {
    const pdfAsset = product.value?.images?.find((image) => {
      return (
        image.hash.endsWith('.pdf') &&
        image.attributes?.ownersManual?.key === 'ownersManual'
      )
    })

    return pdfAsset
      ? `${$config.public.imageBaseUrl}/${pdfAsset?.hash}`
      : undefined
  })

  const complianceUrl = computed<string | undefined>(() => {
    const pdfAsset = product.value?.images?.find((image) => {
      return (
        image.hash.endsWith('.pdf') &&
        (image.attributes?.complianceDeclarations?.key ===
          'complianceDeclaration' ||
          image.attributes?.complianceDeclaration?.key ===
            'complianceDeclaration')
      )
    })

    return pdfAsset
      ? `${$config.public.imageBaseUrl}/${pdfAsset?.hash}`
      : undefined
  })

  // Cart overlay
  const isCartOverlayVisible = ref(false)
  const toggleCartOverlay = (newVal: boolean) => {
    isCartOverlayVisible.value = newVal
  }

  return extendPromise(
    finalPromise.then(() => ({})),
    {
      fetch,
      error,
      status,
      productId,
      backUrl,
      brand,
      brandLogo,
      productName,
      price,
      lowestPriorPrice,
      siblings,
      activeVariant,
      selectedVariant,
      hasOneSizeVariantOnly,
      setVariantId,
      images,
      hasSpecial,
      availableQuantity,
      quantity,
      productCategories,
      breadcrumbs,
      fetching,
      product: computed(() => product.value || undefined),
      salesStatus,
      packageUnit,
      hasSiblings,
      inStock,
      stock,
      isSellableWithoutStock,
      variantsSortedByPrice,
      wishlist,
      isInWishlist,
      currentProductCategory,
      manualUrl,
      complianceUrl,
      // trackAddToCart,
      trackProductView,
      currentVariantReferenceKey,
      deliveryForecast,
      toggleCartOverlay,
      isFromRopoPlp,
    },
  )
}
